
import React from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { AlertCircle, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as Sentry from '@sentry/react';

interface AppErrorBoundaryProps {
    children: React.ReactNode;
    onReset?: () => void;
}

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
    return (
        <div className="p-4 rounded-md border border-destructive/50 bg-destructive/10">
            <div className="flex items-center gap-2 text-destructive mb-2">
                <AlertCircle className="h-5 w-5" />
                <h3 className="font-semibold">Something went wrong</h3>
            </div>
            <p className="text-sm text-destructive-foreground mb-4">
                {error.message || 'An unexpected error occurred.'}
            </p>
            <Button
                onClick={resetErrorBoundary}
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
            >
                <RefreshCcw className="mr-2 h-3 w-3" />
                Try Again
            </Button>
        </div>
    );
}

const logError = (error: Error, info: React.ErrorInfo) => {
    console.error('Caught an error:', error, info);
    
    // Capture error in Sentry with component stack trace
    Sentry.captureException(error, {
        contexts: {
            react: {
                componentStack: info.componentStack,
            },
        },
    });
};

export const AppErrorBoundary: React.FC<AppErrorBoundaryProps> = ({ children, onReset }) => {
    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={logError}
            onReset={onReset}
        >
            {children}
        </ErrorBoundary>
    );
};
